import React, { useEffect, useState, useCallback } from "react";
import "./Dashboard.css";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useEventEmitter } from "../../contexts/EventEmmiter";
import { setIsGenerated as setIsGeneratedAction } from "../../store/appActions";

// Components
import Dropdown from "../../components/GlobalDropdown/GlobalDropdown";
import DossierPreview from "../../components/DossierPreview/DossierPreview";
import WorldOnboardingMap from "../../components/WorldOnboardingMap/WorldOnboardingMap";

// Assets
import resetIcon from "../../assets/images/reset-icon.svg";
import regenerateIcon from "../../assets/images/regenerate-icon.svg";
import countryIcon from "../../assets/images/country-onboarded-icon.svg";
import productionRequestIcon from "../../assets/images/product-request-icon.svg";
import productOnboardedIcon from "../../assets/images/product-onboarded-icon.svg";
import averageTimeSpentIcon from "../../assets/images/average-time-spent-icon.svg";

// constants
import { useConstants } from "../../constant/constantProvider";
import {
    fetchCountryDetails,
    fetchProductDetails,
    fetchRegiObjectiveDetails,
    fetchBinderDetails,
    generateDossierApi,
    downloadDossierApi,
} from "../../store/apiActions";
import { downloadPdf, downloadDocx, downloadFile, generateDossierFileName } from "../../utils/fileHelper";
import { parseDossierResponse } from "../../utils/dossierResponseParser";

export const Dashboard = () => {
    const { dashboard, global } = useConstants();
    const dispatch = useDispatch();
    const { subscribe } = useEventEmitter();

    const [countries, setCountries] = useState([]);
    const [products, setProducts] = useState([]);
    const [regiObjectives, setRegiObjectives] = useState([]);
    const [binders, setBinders] = useState([]);
    const [formData, setFormData] = useState({ country: "", productName: "", registration: "", binderSelection: "" });

    // Generation state
    const [isGenerating, setIsGenerating] = useState(false);
    const [isGenerated, setIsGenerated] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [docxBlob, setDocxBlob] = useState(null);
    const [mainFileName, setMainFileName] = useState("");
    const [applicationsList, setApplicationsList] = useState([]);

    const countryResponse = useSelector((state) => state?.api?.getCountryDetails);
    const productResponse = useSelector((state) => state?.api?.getProductDetails);
    const regiObjectiveResponse = useSelector((state) => state?.api?.getRegiObjectiveDetails);
    const binderResponse = useSelector((state) => state?.api?.getBinderDetails);

    useEffect(() => {
        dispatch(fetchCountryDetails());
    }, [dispatch]);

    useEffect(() => {
        if (countryResponse?.data) {
            const mappedCountries = countryResponse.data.map((item) => ({
                value: item.country_name,
                label: item.country_name?.toUpperCase() || "",
            }));
            setCountries(mappedCountries);
        }
    }, [countryResponse?.data]);

    // Fetch product when country changes
    useEffect(() => {
        if (formData?.country?.trim()) {
            setProducts([]);
            setRegiObjectives([]);
            setBinders([]);
            dispatch(fetchProductDetails({ country: formData.country }));
        }
    }, [formData.country, dispatch]);

    // Fetch registration objective when product changes
    useEffect(() => {
        if (formData?.productName?.trim()) {
            setRegiObjectives([]);
            setBinders([]);
            dispatch(fetchRegiObjectiveDetails({
                country: formData.country,
                formulation_name_clean: formData.productName,
            }));
        }
    }, [formData.productName, formData.country, dispatch]);

    useEffect(() => {
        if (productResponse?.data) {
            const excludedProducts = [
                "acetamiprid_methoxyfenozide_150_240_wg",
                "acifluorfen_clodinafop_propargyl_165_80_ec",
                "propanil_triclopyr_butotyl_380_55_6_ec",
            ];
            const mappedProducts = productResponse.data
                .filter((item) => !excludedProducts.includes(item.formulation_name_clean))
                .map((item) => ({
                    value: item.formulation_name_clean,
                    label: item.formulation_name_clean?.toUpperCase() || "",
                }));
            setProducts(mappedProducts);
        }
    }, [productResponse?.data]);

    useEffect(() => {
        if (regiObjectiveResponse?.data) {
            const mappedRegi = regiObjectiveResponse.data.map((item) => ({
                value: item.registration_objective,
                label: item.registration_objective?.toUpperCase() || "",
            }));
            setRegiObjectives(mappedRegi);
        }
    }, [regiObjectiveResponse?.data]);

    // Fetch binder when registration objective changes
    useEffect(() => {
        if (formData?.registration?.trim()) {
            setBinders([]);
            dispatch(fetchBinderDetails({ registration_objective: formData.registration }));
        }
    }, [formData.registration, dispatch]);

    useEffect(() => {
        if (binderResponse?.data) {
            const mappedBinders = binderResponse.data
                .filter((item) => item?.binder_id_name)
                .map((item) => ({
                    value: item.binder_id_name,
                    label: item.binder_id_name?.toUpperCase() || "",
                }));
            setBinders(mappedBinders);
        }
    }, [binderResponse?.data]);

    const hasAnySelection = !!(formData.country || formData.productName || formData.registration || formData.binderSelection);
    const isFormValid = Boolean(
        formData.country &&
        formData.productName &&
        formData.registration &&
        formData.binderSelection
    );

    // Test flag: set to true to autofill test payload on generate, false for normal behavior
    const IS_TEST = false;

    // Available test payloads
    const TEST_PAYLOADS = {
        test1_zambia: {
            name: "Test 1 (Zambia)",
            country: "Zambia",
            formulation_name_clean: "METHOXYFENOZIDE(240)SC",
            registration_objective: "REG-ZM-171-001",
            binder_id_name: "RegDS-01436",
        },
        test2_argentina: {
            name: "Test 2 (Argentina)",
            country: "Argentina",
            formulation_name_clean: "MANCOZEB(800)WP",
            registration_objective: "REG-AR-407-002",
            binder_id_name: "RegDS-00694",
        },
    };

    const TEST_PAYLOAD = TEST_PAYLOADS.test1_zambia;

    console.log(`🧪 [Dashboard] IS_TEST mode active — using ${TEST_PAYLOAD.name} test payload if form fields are empty:`, TEST_PAYLOAD);

    const isFormLocked = isGenerating || isGenerated;

    // ── Onboarded countries — sourced from the country API response ────────────
    // WorldOnboardingMap resolves each name to an ISO code internally.
    // Replace countryResponse.data with a dedicated onboarding API when available.
    const onboardedCountries = (countryResponse?.data || []).map((item) => ({
        name: item.country_name,
    }));

    const statCards = [
        { icon: countryIcon, alt: "Countries", value: String(countryResponse?.data?.length ?? 0), labelKey: "COUNTRIES_ONBOARDED", colorClass: "stat-card-icon--blue" },
        { icon: productionRequestIcon, alt: "Production Requests", value: "3", labelKey: "PRODUCTION_REQUESTS", colorClass: "stat-card-icon--orange" },
        { icon: productOnboardedIcon, alt: "Products Onboarded", value: "24", labelKey: "PRODUCTS_ONBOARDED", colorClass: "stat-card-icon--purple" },
        { icon: averageTimeSpentIcon, alt: "Time Saved", value: "16 h", labelKey: "AVG_TIME_SAVED", colorClass: "stat-card-icon--green" },
    ];

    // Track per-app selected preview URL (for ZIP multi-file navigation)
    const [selectedAppPreviewUrl, setSelectedAppPreviewUrl] = useState(null);

    const handleGenerate = async (customPayload = null) => {
        if (isGenerating) return;
        if (!IS_TEST && !isFormValid && !customPayload) return;

        // Log active test payload being used
        if (IS_TEST && !customPayload) {
            console.log(`🌍 [Dashboard] Using ${TEST_PAYLOAD.name} test payload for generation:`, {
                country: formData.country || TEST_PAYLOAD.country,
                formulation_name_clean: formData.productName || TEST_PAYLOAD.formulation_name_clean,
                registration_objective: formData.registration || TEST_PAYLOAD.registration_objective,
                binder_id_name: formData.binderSelection || TEST_PAYLOAD.binder_id_name,
            });
        }

        setIsGenerating(true);
        setIsGenerated(false);
        setPdfUrl(null);

        const requestBody = customPayload || (IS_TEST ? {
            country: formData.country || TEST_PAYLOAD.country,
            formulation_name_clean: formData.productName || TEST_PAYLOAD.formulation_name_clean,
            registration_objective: formData.registration || TEST_PAYLOAD.registration_objective,
            binder_id_name: formData.binderSelection || TEST_PAYLOAD.binder_id_name,
        } : {
            country: formData.country,
            formulation_name_clean: formData.productName,
            registration_objective: formData.registration,
            binder_id_name: formData.binderSelection,
        });

        // In test mode: if form fields were empty, fill with test payload for preview consistency
        if (IS_TEST && !formData.productName) {
            setFormData({
                country: requestBody.country,
                productName: requestBody.formulation_name_clean,
                registration: requestBody.registration_objective,
                binderSelection: requestBody.binder_id_name,
            });
        }

        try {
            const responseData = await dispatch(generateDossierApi(requestBody));

            if (responseData && !responseData.error && !(responseData instanceof Error)) {
                const actualData = responseData.data || responseData;

                // Parse response (handles ZIP, multipart/form-data, encrypted JSON, base64, or binary PDF)
                const parsed = await parseDossierResponse(actualData);

                // Applications Mapping Strategy
                let mappedApps = [];
                if (parsed.applications && Array.isArray(parsed.applications) && parsed.applications.length > 0) {
                    mappedApps = parsed.applications;
                } else if (actualData?.applications && Array.isArray(actualData.applications) && actualData.applications.length > 0) {
                    mappedApps = actualData.applications;
                } else if (binderResponse?.data && Array.isArray(binderResponse.data) && binderResponse.data.length > 0) {
                    mappedApps = binderResponse.data
                        .filter((item) => item?.binder_id_name)
                        .map((item, idx) => ({
                            id: item.id || idx + 1,
                            name: item.binder_id_name,
                            title: formData.registration || item.binder_id_name,
                        }));
                } else {
                    mappedApps = [
                        {
                            id: 1,
                            name: requestBody.binder_id_name || requestBody.formulation_name_clean,
                            title: requestBody.registration_objective || requestBody.formulation_name_clean,
                        },
                    ];
                }

                setApplicationsList(mappedApps);
                setMainFileName(parsed.mainFileName || "");

                // --- ZIP multi-file: each app already has its own blob, do NOT set global pdfUrl ---
                if (parsed.isZip && parsed.applications && parsed.applications.length > 0) {
                    console.log("📦 [Dashboard] ZIP response — all files available individually in applications list:", parsed.applications.map(a => a.name));
                    // Set global docxBlob as fallback but let per-app selection drive the viewer
                    setPdfUrl(null);
                    setSelectedAppPreviewUrl(null);
                    setDocxBlob(parsed.docxBlob || null);
                } else if (parsed.isPdf && parsed.pdfBlob) {
                    const fileURL = URL.createObjectURL(parsed.pdfBlob);
                    setPdfUrl(fileURL);
                    setSelectedAppPreviewUrl(null);
                    setDocxBlob(parsed.docxBlob || null);
                } else if (parsed.docxBlob) {
                    setPdfUrl(null);
                    setSelectedAppPreviewUrl(null);
                    setDocxBlob(parsed.docxBlob);
                } else {
                    const file = parsed.pdfBlob || (actualData instanceof Blob ? actualData : new Blob([actualData], { type: "application/pdf" }));
                    const fileURL = URL.createObjectURL(file);
                    setPdfUrl(fileURL);
                    setSelectedAppPreviewUrl(null);
                }

                setIsGenerated(true);
                dispatch(setIsGeneratedAction(true));
            } else {
                const errorMsg =
                    responseData?.error?.message ||
                    responseData?.error?.originalError?.message ||
                    "Failed to generate dossier. Please try again.";
                console.error("❌ [Generate Dossier] Failed with responseData error:", {
                    errorMsg,
                    error: responseData?.error,
                    responseData
                });
                toast.error(errorMsg);
                setIsGenerated(false);
                dispatch(setIsGeneratedAction(false));
            }
        } catch (error) {
            console.error("❌ [Generate Dossier] Exception caught:", error);
            toast.error(error?.message || "Failed to generate dossier. Please try again.");
            setIsGenerated(false);
            dispatch(setIsGeneratedAction(false));
        } finally {
            setIsGenerating(false);
        }
    };

    const handleReset = useCallback(() => {
        setFormData({ country: "", productName: "", registration: "", binderSelection: "" });
        setProducts([]);
        setRegiObjectives([]);
        setBinders([]);
        setApplicationsList([]);
        setIsGenerating(false);
        setIsGenerated(false);
        setDocxBlob(null);
        setMainFileName("");
        setSelectedAppPreviewUrl(null);
        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
        dispatch(setIsGeneratedAction(false));
    }, [pdfUrl, dispatch]);

    useEffect(() => {
        const unsubscribe = subscribe("RESET_DASHBOARD", () => {
            handleReset();
        });
        return unsubscribe;
    }, [subscribe, handleReset]);

    const handleNewDossier = () => {
        handleReset();
    };

    const handleDownloadPdf = (singleApp = null) => {
        if (singleApp?.blob) {
            downloadFile(singleApp.blob, singleApp.fileName || singleApp.name || "application", singleApp.mimeType);
            return;
        }
        if (pdfUrl) {
            const fileName = generateDossierFileName({
                country: formData.country,
                productName: formData.productName,
                customName: singleApp?.name,
                extension: "pdf",
            });
            downloadPdf(pdfUrl, fileName);
        } else if (docxBlob) {
            const fileName = mainFileName || generateDossierFileName({
                country: formData.country,
                productName: formData.productName,
                extension: "docx",
            });
            downloadDocx(docxBlob, fileName);
        }
    };

    // Called when user selects an app in DossierPreview sidebar — switches the document viewer
    const handleAppSelect = (app) => {
        console.log("📄 [Dashboard] App selected for preview:", app?.name, "| type:", app?.mimeType);
        if (app?.isPdf && app?.blob) {
            // Revoke previous selected-app preview URL to avoid memory leaks
            if (selectedAppPreviewUrl) URL.revokeObjectURL(selectedAppPreviewUrl);
            const url = URL.createObjectURL(app.blob);
            setSelectedAppPreviewUrl(url);
        } else {
            setSelectedAppPreviewUrl(null);
        }
    };

    const handleDownloadDocs = async () => {
        if (docxBlob) {
            const fileName = mainFileName || generateDossierFileName({
                country: formData.country,
                productName: formData.productName,
                extension: "docx",
            });
            downloadDocx(docxBlob, fileName);
            return;
        }

        const requestBody = {
            country: formData.country,
            formulation_name_clean: formData.productName,
            registration_objective: formData.registration,
            binder_id_name: formData.binderSelection,
        };

        try {
            const responseData = await dispatch(downloadDossierApi(requestBody));
            if (responseData && !responseData.error && !(responseData instanceof Error)) {
                const actualData = responseData.data || responseData;
                const fileName = generateDossierFileName({
                    country: formData.country,
                    productName: formData.productName,
                    extension: "docx",
                });
                downloadDocx(actualData, fileName);
            } else {
                const errorMsg =
                    responseData?.error?.message ||
                    responseData?.error?.originalError?.message ||
                    "Failed to download DOCX. Please try again.";
                toast.error(errorMsg);
            }
        } catch (error) {
            console.error("Download docs error:", error);
            toast.error(error?.message || "Failed to download DOCX. Please try again.");
        }
    };

    return (
        <>
            <div className="dashboard-main-container">
                <div className="dashboard-inner-container">
                    <div className="dasboard-top-fixed-section">
                        <div className="heading">{dashboard.TITLE}</div>

                        <div className="dropdowns-container">
                            <div className="custom-select-wrapper">
                                <Dropdown
                                    placeholder={dashboard.SELECT_COUNTRY}
                                    options={countries}
                                    enableSearchBar={true}
                                    isLoading={countryResponse?.loading}
                                    isDisabled={isFormLocked}
                                    value={formData.country}
                                    onChange={(val) => {
                                        setFormData({ country: val || "", productName: "", registration: "", binderSelection: "" });
                                        setProducts([]);
                                        setRegiObjectives([]);
                                        setBinders([]);
                                    }}
                                />
                            </div>
                            <div className="custom-select-wrapper">
                                <Dropdown
                                    placeholder={dashboard.SELECT_PRODUCT}
                                    options={products}
                                    enableSearchBar={true}
                                    isLoading={productResponse?.loading}
                                    isDisabled={!formData.country || isFormLocked}
                                    value={formData.productName}
                                    onChange={(val) => {
                                        setFormData({ ...formData, productName: val || "", registration: "", binderSelection: "" });
                                        setRegiObjectives([]);
                                        setBinders([]);
                                    }}
                                />
                            </div>
                            <div className="custom-select-wrapper">
                                <Dropdown
                                    placeholder={dashboard.SELECT_OBJECTIVE}
                                    options={regiObjectives}
                                    enableSearchBar={true}
                                    isLoading={regiObjectiveResponse?.loading}
                                    isDisabled={!formData.productName || isFormLocked}
                                    value={formData.registration}
                                    onChange={(val) => {
                                        setFormData({ ...formData, registration: val, binderSelection: "" });
                                        setBinders([]);
                                    }}
                                />
                            </div>
                            <div className="custom-select-wrapper">
                                <Dropdown
                                    placeholder={dashboard.SELECT_BINDER}
                                    options={binders}
                                    enableSearchBar={true}
                                    isLoading={binderResponse?.loading}
                                    isDisabled={!formData.registration || isFormLocked}
                                    value={formData.binderSelection}
                                    onChange={(val) => setFormData({ ...formData, binderSelection: val })}
                                />
                            </div>
                        </div>

                        <div className="buttons-container">
                            {isGenerated ? (
                                <button
                                    className="button-style outline-button"
                                    onClick={handleNewDossier}
                                >
                                    <img src={regenerateIcon} alt="" className="reset-icon" style={{ opacity: 1 }} />
                                    {dashboard.GENERATE_NEW_DOSSIER || "Generate New Dossier"}
                                </button>
                            ) : (
                                <>
                                    <button
                                        className={`button-style primary-button ${
                                            isGenerating
                                                ? "dashboard-generating-btn"
                                                : (!IS_TEST && !isFormValid ? "disabled-button" : "")
                                        }`}
                                        disabled={isGenerating || (!IS_TEST && !isFormValid)}
                                        onClick={() => handleGenerate()}
                                    >
                                        {isGenerating ? (
                                            <>
                                                <span className="dashboard-spinner" />
                                                {dashboard.GENERATING || "Generating..."}
                                            </>
                                        ) : (
                                            dashboard.GENERATE
                                        )}
                                    </button>
                                    <button
                                        className={`button-style outline-button ${!hasAnySelection || isGenerating ? "disabled-button" : ""}`}
                                        onClick={handleReset}
                                        disabled={!hasAnySelection || isGenerating}
                                    >
                                        <img src={resetIcon} alt="Reset" className="reset-icon" />
                                        {global.RESET}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* ── Dossier Preview (Rendered in its own separate container outside) ── */}
                    {(isGenerating || isGenerated) && (
                        <>
                            <div className="splitter"></div>
                            <div className="dashboard-dossier-prev-section">
                <DossierPreview
                                    isLoading={isGenerating}
                                    pdfUrl={selectedAppPreviewUrl || pdfUrl}
                                    docxBlob={docxBlob}
                                    mainFileName={mainFileName}
                                    applications={applicationsList}
                                    handleDownloadPdf={handleDownloadPdf}
                                    handleDownloadDocs={handleDownloadDocs}
                                    onAppSelect={handleAppSelect}
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* ── Stat Cards (Rendered in its own separate container outside) ── */}
                {!(isGenerating || isGenerated) && (
                    <>
                        <div className="stat-cards-container">
                            {statCards.map(({ icon, alt, value, labelKey, colorClass }) => (
                                <div className="stat-card" key={labelKey}>
                                    <div className={`stat-card-icon-wrapper ${colorClass}`}>
                                        {icon && <img src={icon} alt={alt} className="stat-card-icon" />}
                                    </div>
                                    <div className="stat-card-content">
                                        <span className="stat-card-value">{value}</span>
                                        <span className="stat-card-label">{dashboard[labelKey]}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* ── World Onboarding Map ── */}
                        <WorldOnboardingMap onboardedCountries={onboardedCountries} />
                    </>
                )}
            </div>

        </>
    );
};

export default Dashboard;